import { useEffect, useState } from 'react';
import { BASE_CHECK_MS, isNewerBundle, MIN_CHECK_GAP_MS, nextCheckDelay, parseServedVersion, readOwnBundleSha, VERSION_PATH } from '../lib/bundleVersion';
/**
 * Notices when dock-one is serving a newer bundle than the one this tab
 * loaded. Checks on focus / visibility and every few minutes; backs off on
 * errors; does nothing while the tab is hidden.
 */ export function useBundleVersionCheck(options = {}) {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    useEffect(()=>{
        const fetchImpl = options.fetchImpl ?? fetch.bind(window);
        const readOwn = options.readOwn ?? readOwnBundleSha;
        const isHidden = options.isHidden ?? (()=>document.visibilityState === 'hidden');
        const own = readOwn();
        if (!own) {
            return;
        }
        let failures = 0;
        let timer;
        let lastCheckAt = 0;
        let cancelled = false;
        const schedule = (delay)=>{
            if (timer !== undefined) {
                clearTimeout(timer);
            }
            timer = setTimeout(()=>{
                void runCheck(false);
            }, delay);
        };
        const runCheck = async (force)=>{
            if (cancelled || isHidden()) {
                return;
            }
            const now = Date.now();
            if (!force && now - lastCheckAt < MIN_CHECK_GAP_MS) {
                return;
            }
            lastCheckAt = now;
            try {
                const response = await fetchImpl(VERSION_PATH, {
                    cache: 'no-store',
                    method: 'GET'
                });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                const served = parseServedVersion(await response.json());
                if (served === null) {
                    throw new Error('invalid version payload');
                }
                if (cancelled) {
                    return;
                }
                failures = 0;
                if (isNewerBundle(own, served)) {
                    setUpdateAvailable(true);
                    return;
                }
                schedule(nextCheckDelay(0, BASE_CHECK_MS));
            } catch  {
                if (cancelled) {
                    return;
                }
                failures += 1;
                schedule(nextCheckDelay(failures, BASE_CHECK_MS));
            }
        };
        const onVisible = ()=>{
            if (!isHidden()) {
                void runCheck(true);
            }
        };
        void runCheck(true);
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        return ()=>{
            cancelled = true;
            if (timer !== undefined) {
                clearTimeout(timer);
            }
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, [
        options.fetchImpl,
        options.isHidden,
        options.readOwn
    ]);
    return {
        updateAvailable
    };
}
